import { agreementsService } from '../../services/agreementsService'
import { ACTIVE_STATES, leaseState } from '../profile/leasePicker'

/** Where a tenant with a lease lives in the app. */
export const TENANT_HOME = '/profile/tenant-dashboard'

/**
 * A destination to return to after signing in, or null. Only same-origin
 * relative paths pass ("/agreement/abc?x=1#y"): never another origin
 * ("//evil.com", "/\evil.com", "https://..."), never the auth pages
 * themselves. Accepts a path string or a router location object.
 */
export function safeReturnPath(value) {
  let path = value
  if (path && typeof path === 'object') {
    path = `${path.pathname || ''}${path.search || ''}${path.hash || ''}`
  }
  if (typeof path !== 'string') return null
  path = path.trim()
  if (!path.startsWith('/')) return null
  if (path.startsWith('//') || path.startsWith('/\\')) return null
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f]/.test(path)) return null
  if (/^\/(login|register)(?:[/?#]|$)/.test(path)) return null
  return path
}

/**
 * The destination the sign-in page was sent with: `state.from` (set by the
 * protected routes) or `?next=` (links in invite pages), whichever is safe.
 */
export function requestedReturnPath(location) {
  const fromState = safeReturnPath(location?.state?.from)
  if (fromState) return fromState
  let next = null
  try {
    next = new URLSearchParams(location?.search || '').get('next')
  } catch {
    next = null
  }
  return safeReturnPath(next)
}

/**
 * Props for a <Link> between the sign-in and sign-up pages that keep the
 * destination: `state.from` and `?next=` ride along, so a visitor who
 * switches pages still comes back to where they were going.
 */
export function authLinkProps(location, pathname) {
  const from = safeReturnPath(location?.state?.from)
  const next = requestedReturnPath({ search: location?.search })
  return {
    to: { pathname, search: next ? `?next=${encodeURIComponent(next)}` : '' },
    state: from ? { from: location.state.from } : undefined,
  }
}

/**
 * A signed lease that is in force today, ending, or starting soon: the
 * tenant has a home on Rentra, so their dashboard is the place to land.
 */
export function hasCurrentLease(agreements, now = new Date()) {
  return (agreements || []).some(
    a => a && ACTIVE_STATES.includes(leaseState(a, now))
  )
}

/** Fetch the signed-in tenant's leases and answer hasCurrentLease. */
export async function fetchHasCurrentLease() {
  try {
    return hasCurrentLease(await agreementsService.listAgreements())
  } catch {
    return false
  }
}

/**
 * Where a signed-in user lands when nothing asked for a page: landlords on
 * their dashboard, tenants with a lease on theirs, everyone else on the
 * home page (which sends guarantors to /cosigner and shows tenants Browse).
 */
export async function landingPathFor(user) {
  if (!user) return '/'
  if (user.userType === 'owner') return '/dashboard'
  if (user.userType === 'student' && (await fetchHasCurrentLease())) {
    return TENANT_HOME
  }
  return '/'
}
